import React, { useState } from 'react';
import moment from 'moment';
import PriceHistoryChart from './PriceHistoryChart';
import { PriceHistoryChartData, PriceHistoryIndicator } from '../types/company';

interface PriceHistoryCardProps {
  priceHistoryChartData: PriceHistoryChartData;
  priceHistoryIndicator: PriceHistoryIndicator;
  onDateRangeChange?: (value: string) => void;
}

const dateRangeOptions = [
  { label: '1m', value: '1m' },
  { label: '3m', value: '3m' },
  { label: 'YTD', value: 'ytd' },
  { label: '1y', value: '1y' },
  { label: '2y', value: '2y' },
  { label: 'All', value: 'all' }
];

const eMountMap: Record<string, string> = {
  '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'May', '06': 'Jun',
  '07': 'Jul', '08': 'Aug', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec'
};

const PriceHistoryCard: React.FC<PriceHistoryCardProps> = ({
  priceHistoryChartData,
  priceHistoryIndicator,
  onDateRangeChange
}) => {
  const [selectedDateRange, setSelectedDateRange] = useState('all');

  const formatDollar = (value: number | null, position?: number, unit?: string) => {
    if (value === null) return '-';
    if (position) {
      return `$${(value / position).toFixed(2)}${unit}`;
    }
    return `$${value.toFixed(2)}${unit || ''}`;
  };

  const formatDate = (value: string) => {
    const date = moment(value).format('yyyy-MM-DD');
    const [year, month, day] = date.split('-');
    return `${eMountMap[month]} ${day}th ${year}`;
  };

  const handleDateRangeChange = (value: string) => {
    if (selectedDateRange !== value) {
      setSelectedDateRange(value);
      onDateRangeChange?.(value);
    }
  };

  return (
    <div className="mt-[1.55172rem] px-4 sm:px-12 md:px-12">
      <div className="border border-gray-200 rounded-lg">
        <div className="p-4 border-b border-gray-200 text-lg font-semibold">Price History</div>
        <div className="p-4">
          <div className="flex items-end justify-start">
            <div className="mr-10">
              <div className="text-base">{formatDollar(priceHistoryIndicator.price, 1e9, '')}</div>
              <div className="text-sm text-gray-600">Est. Val. {formatDollar(priceHistoryIndicator.val, 1e9, '')}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">
                {formatDate(priceHistoryIndicator.date[0])} - {formatDate(priceHistoryIndicator.date[1])}
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center flex-wrap mt-2.5">
            <div className="flex gap-1">
              {dateRangeOptions.map((item, index) => (
                <button
                  key={index}
                  className={`px-2 py-1 text-xs rounded ${
                    item.value === selectedDateRange
                      ? 'bg-[#00A0DC] text-white'
                      : 'bg-transparent hover:bg-gray-100'
                  }`}
                  onClick={() => handleDateRangeChange(item.value)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="text-xs text-gray-600">
              {formatDate(priceHistoryChartData.dates[0])} - {formatDate(priceHistoryChartData.dates[priceHistoryChartData.dates.length - 1])}
            </div>
          </div>
          <div className="w-full h-[260px] mt-4">
             <PriceHistoryChart chartData={priceHistoryChartData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceHistoryCard;
